import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {Store} from "@ngrx/store";
import {take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {DtoTrumpfCompanyResponseResultsInterface, TrumpfCompanyModel} from "../../../interfaces";
import {mapTrumpfCompanyFromDtoArray, mapTrumpfCompanyToDto} from "../../../mapping/trumpf-company.mapping";
import {
  addTrumpfCompanies,
  addTrumpfCompany,
  setPaginatedTrumpfCompanies,
  setTrumpfCompanies,
  setTrumpfCompaniesPaginationInfo,
  updateTrumpfCompany
} from "../../../store/actions/trumpf-companies.actions";
import {selectTrumpfCompanyFilters} from "../../../store/reducers/trumpf-companies.reducer";
import {ToastsService} from "../../app-services";

const pageSize = APP_CONSTANTS.PAGINATION_PAGE_SIZE;

@Injectable({
  providedIn: "root"
})
export class TrumpfCompaniesService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly toastsService = inject(ToastsService);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);

  constructor() {
    this.store.select(selectTrumpfCompanyFilters).subscribe(() => {
      this.getTrumpfCompaniesPaginated();
    });
  }

  public getAllTrumpfCompanies() {
    this.store.dispatch(setTrumpfCompanies({trumpfCompanies: []}));
    this.getTrumpfCompaniesRecursive(0);
  }

  public getTrumpfCompaniesPaginated(): void {
    this.store
      .select(selectTrumpfCompanyFilters)
      .pipe(take(1))
      .subscribe(filters => {
        const queryParams = prepareFiltersForParams(filters);
        this.httpClient
          .get<DtoTrumpfCompanyResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.TRUMPF_COMPANIES, {
            params: queryParams
          })
          .subscribe({
            next: data => {
              this.store.dispatch(setPaginatedTrumpfCompanies({trumpfCompanies: mapTrumpfCompanyFromDtoArray(data.results ?? [])}));
              this.store.dispatch(
                setTrumpfCompaniesPaginationInfo({
                  paginationInfo: {
                    from: (data.offset ?? 0) + 1,
                    to: (data.offset ?? 0) + (data.count ?? 0),
                    totalResults: data.totalCount ?? 0,
                    currentPage: (data.offset ?? 0) / pageSize + 1,
                    totalPages: Math.ceil((data.totalCount ?? 0) / pageSize) + 1
                  }
                })
              );
            },
            error: (error: HttpErrorResponse) => {
              this.toastsService.addToast({
                severity: ToastSeverityEnum.Error,
                message: "Error fetching TRUMPF Companies Paginated, Status " + error.status
              });
            }
          });
      });
  }

  private getTrumpfCompaniesRecursive(offset: number) {
    this.httpClient
      .get<DtoTrumpfCompanyResponseResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.TRUMPF_COMPANIES, {
        params: {offset: offset}
      })
      .subscribe({
        next: data => {
          this.store.dispatch(addTrumpfCompanies({trumpfCompanies: mapTrumpfCompanyFromDtoArray(data.results ?? [])}));
          if (data.next) {
            this.getTrumpfCompaniesRecursive(offset + APP_CONSTANTS.MAX_API_RESULTS_LIMIT);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: "Error fetching TRUMPF Companies, Status " + error.status
          });
        }
      });
  }

  public createTrumpfCompany(model: TrumpfCompanyModel): void {
    const shortCodeEncoded = encodeURIComponent(model.shortCode ?? "");
    this.httpClient
      .put(this.assetManagementUrl + API_ENDPOINTS.TRUMPF_COMPANIES + `/${shortCodeEncoded}`, mapTrumpfCompanyToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(addTrumpfCompany({trumpfCompany: model}));
          this.getTrumpfCompaniesPaginated();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Created new Trumpf Company ${model.shortCode}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to create new Trumpf Company ${model.shortCode}, Status ${error.status}`
          });
        }
      });
  }

  public updateTrumpfCompany(model: TrumpfCompanyModel): void {
    const shortCodeEncoded = encodeURIComponent(model.shortCode ?? "");
    this.httpClient
      .post(this.assetManagementUrl + API_ENDPOINTS.TRUMPF_COMPANIES + `/${shortCodeEncoded}`, mapTrumpfCompanyToDto(model))
      .subscribe({
        complete: () => {
          this.store.dispatch(updateTrumpfCompany({trumpfCompany: model}));
          this.getTrumpfCompaniesPaginated();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Updated Trumpf Company ${model.shortCode}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to update Trumpf Company ${model.shortCode}, Status ${error.status}`
          });
        }
      });
  }

  public deleteTrumpfCompany(model: TrumpfCompanyModel): void {
    const shortCodeEncoded = encodeURIComponent(model.shortCode ?? "");
    this.httpClient.delete(this.assetManagementUrl + API_ENDPOINTS.TRUMPF_COMPANIES + `/${shortCodeEncoded}`).subscribe({
      complete: () => {
        this.getTrumpfCompaniesPaginated();
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: `Deleted Trumpf Company ${model.shortCode}`
        });
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to delete Trumpf Company ${model.shortCode}, Status ${error.status}`
        });
      }
    });
  }
}
